import React from 'react';
import { Briefcase, MessagesSquare, UserPlus, Send, Building2, Tent, FileCheck2 } from 'lucide-react';
import HelpTip from '../../../components/HelpTip';

const CARDS = [
  { key: 'placed', label: 'Placed (hired)', help: 'kpi_placed', Icon: Briefcase },
  { key: 'career', label: 'Career guidance', help: 'career', Icon: MessagesSquare },
  { key: 'registered', label: 'Job seekers registered', help: 'kpi_registered', Icon: UserPlus },
  { key: 'referred', label: 'Referred to employers', help: 'kpi_referred', Icon: Send },
  { key: 'vacancies', label: 'Vacancies solicited', help: 'kpi_vacancies', Icon: Building2 },
  { key: 'job_fairs', label: 'Job fairs this year', help: 'prog_jobfair', Icon: Tent },
];

function KindBadge({ kind }) {
  const cls = kind === 'DOLE' ? 'text-blue-700 bg-blue-50' : 'text-slate-600 bg-slate-100';
  return <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${cls}`}>{kind === 'DOLE' ? 'DOLE goal' : 'SPRS target'}</span>;
}

function Card({ card, def }) {
  const { Icon } = def;
  const unset = card.unset;
  const pct = card.goal ? Math.min(100, (card.value / card.goal) * 100) : 0;
  const barColor = card.onTrack ? '#059669' : (pct >= 60 ? '#0284c7' : '#d97706');
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 hover:border-blue-400 hover:shadow-sm transition">
      <div className="flex items-center justify-between gap-1">
        <span className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
          <Icon className="h-3.5 w-3.5" /> {def.label} <HelpTip id={def.help} />
        </span>
        {unset
          ? <span className="text-[10px] font-medium text-slate-400 shrink-0">no target</span>
          : (card.onTrack
            ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded shrink-0">On track</span>
            : <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded shrink-0">Behind</span>)}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-2xl font-extrabold text-slate-900 tabular-nums">{card.value}</span>
        {!unset && <span className="text-xs text-slate-400">/ {card.goal}{card.annual ? ' this year' : ''}</span>}
      </div>
      {!unset && (
        <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden">
          <div style={{ width: `${pct}%`, background: barColor }} className="h-full rounded-full" />
        </div>
      )}
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        <KindBadge kind={card.goalKind} />
        <span className="text-[11px] text-slate-500">{unset ? 'Set your annual target in “Set once a year” below' : card.note}</span>
      </div>
    </div>
  );
}

/**
 * The monthly goal cards. Every card is DOLE-fixed ("DOLE goal") or the office's annual
 * SPRS target ÷ 12 ("SPRS target"); an unset SPRS target shows a prompt, never a fake /0.
 */
export default function GoalCards({ monthly }) {
  const filed = monthly?.lmi_this_month;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {CARDS.map((def) => monthly?.[def.key] && <Card key={def.key} def={def} card={monthly[def.key]} />)}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
            <FileCheck2 className="h-3.5 w-3.5" /> LMI report packet <HelpTip id="kpi_lmi" />
          </span>
        </div>
        <div className={`mt-2 text-sm font-semibold ${filed === null || filed === undefined ? 'text-slate-500' : (filed ? 'text-emerald-700' : 'text-rose-700')}`}>
          {filed === null || filed === undefined ? 'Not marked yet' : (filed ? 'Filed on time ✓' : 'Not filed on time')}
        </div>
        <p className="text-[11px] text-slate-500 mt-1">Mark it on time in the entry panel to earn the 15-pt LMI line.</p>
      </div>
    </div>
  );
}
